import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Users, Zap, Leaf, MapPin, Clock } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

interface JobPosting {
  id: number;
  title: string;
  department: string;
  location: string;
  type: string;
  salary: string;
  description: string;
  requirements: string[];
  questions?: string[];
  postingDate?: string | null;
  expiryDate?: string | null;
  isActive: number;
  uniqueId?: string;
}

const culture = [
  { icon: Users, title: 'Great people', desc: 'We hire curious, kind, and ambitious builders who lift each other up.' },
  { icon: Zap, title: 'Real impact', desc: "You'll work on meaningful products for real customers, not just slide decks." },
  { icon: Leaf, title: 'Flexible by design', desc: 'We value focus, autonomy, and balance — because great work needs a healthy team.' },
];

const Careers = () => {
  const [openPositions, setOpenPositions] = useState<JobPosting[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchJobs();
  }, []);

  const fetchJobs = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/jobs`);
      if (!res.ok) throw new Error('Failed to fetch jobs');
      const data = await res.json();
      setOpenPositions(
        (data || []).map((job: any) => ({
          ...job,
          requirements: job.requirements || [],
          questions: job.questions || [],
          postingDate: job.postingDate || null,
          expiryDate: job.expiryDate || null,
        }))
      );
    } catch (err: any) {
      setError(err.message);
      console.error('Error fetching jobs:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-zinc-100 relative">
      <div className="absolute inset-0 pointer-events-none" style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,0.025) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.025) 1px, transparent 1px)', backgroundSize: '48px 48px' }} />

      <header className="relative z-20 px-4 sm:px-6 lg:px-8 pt-5">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-between items-center rounded-xl border border-zinc-800 bg-[#0d0d0d]/90 backdrop-blur px-5 py-3">
            <Link to="/" className="flex items-center">
              <img src="/pattrnworks_logo_transparent_small.png" alt="PattrnWorks" className="h-9 brightness-0 invert" />
            </Link>
            <nav className="flex items-center space-x-6">
              <Link to="/" className="text-sm text-zinc-400 hover:text-zinc-100 transition-colors duration-200">Home</Link>
              <Link to="/projects" className="text-sm text-zinc-400 hover:text-zinc-100 transition-colors duration-200">Work</Link>
              <Link to="/#contact" className="hidden sm:flex bg-zinc-100 hover:bg-violet-400 text-zinc-900 text-sm font-medium px-4 py-2 rounded-md transition-colors duration-200 items-center">
                Start a project
                <ArrowRight className="ml-1.5 h-4 w-4" />
              </Link>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative z-10 pt-16 pb-10 px-4 md:px-8">
        <div className="max-w-5xl mx-auto">
          <span className="font-mono text-xs tracking-[0.2em] text-violet-400 uppercase">{'// Careers at PattrnWorks'}</span>
          <h1 className="mt-4 text-4xl sm:text-5xl md:text-6xl font-bold tracking-tight text-zinc-100">
            Build the future with us
          </h1>
          <p className="mt-6 text-lg text-zinc-400 max-w-xl leading-relaxed">
            We're a product-focused dev agency helping ambitious companies turn ideas into high-quality software.
            Great people build great products — come be one of them.
          </p>
        </div>
      </section>

      {/* Culture cards */}
      <section className="relative z-10 px-4 md:px-8 pb-16">
        <div className="max-w-5xl mx-auto grid md:grid-cols-3 gap-5">
          {culture.map((c) => {
            const Icon = c.icon;
            return (
              <div key={c.title} className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-7">
                <Icon className="w-6 h-6 text-violet-400 mb-5" />
                <h3 className="text-base font-semibold text-zinc-100 mb-2">{c.title}</h3>
                <p className="text-sm text-zinc-400 leading-relaxed">{c.desc}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Open roles */}
      <section className="relative z-10 px-4 md:px-8 pb-24">
        <div className="max-w-5xl mx-auto">
          <span className="font-mono text-xs tracking-[0.2em] text-violet-400 uppercase">{'// Open roles'}</span>
          <h2 className="mt-3 text-3xl font-bold text-zinc-100">Join our team</h2>
          <p className="mt-2 text-sm text-zinc-500">We're always looking for talented people to help us build what's next.</p>

          {loading ? (
            <div className="py-16 text-center text-zinc-500 font-mono text-sm">Loading roles…</div>
          ) : error ? (
            <div className="py-16 text-center text-zinc-500 font-mono text-sm">Couldn't load openings right now — try again later.</div>
          ) : openPositions.length === 0 ? (
            <div className="py-16 text-center text-zinc-500 font-mono text-sm">No open roles at the moment — check back soon.</div>
          ) : (
            <div className="mt-8 divide-y divide-zinc-800 border-t border-b border-zinc-800">
              {openPositions.map((position) => (
                <div key={position.id} className="py-5 flex flex-col md:flex-row md:items-center gap-4">
                  <Link
                    to={position.uniqueId ? `/careers/job/${position.uniqueId}` : '/careers'}
                    className="flex-1 text-base font-semibold text-zinc-100 hover:text-violet-400 transition-colors"
                  >
                    {position.title}
                  </Link>
                  <div className="flex items-center gap-3">
                    {position.location && (
                      <span className="flex items-center gap-1.5 text-xs text-zinc-400 border border-zinc-800 bg-zinc-900/60 px-3 py-1.5 rounded-md">
                        <MapPin className="w-3.5 h-3.5 text-violet-400" />{position.location}
                      </span>
                    )}
                    {position.type && (
                      <span className="flex items-center gap-1.5 text-xs text-zinc-400 border border-zinc-800 bg-zinc-900/60 px-3 py-1.5 rounded-md">
                        <Clock className="w-3.5 h-3.5 text-violet-400" />{position.type}
                      </span>
                    )}
                    <Link
                      to={position.uniqueId ? `/apply/${position.uniqueId}` : '/careers/apply'}
                      state={position.uniqueId ? undefined : { position: position.title, jobId: position.id, questions: position.questions || [] }}
                      className="bg-zinc-100 hover:bg-violet-400 text-zinc-900 text-sm font-medium px-4 py-2 rounded-md transition-colors duration-200 flex items-center whitespace-nowrap"
                    >
                      Apply <ArrowRight className="ml-1.5 h-4 w-4" />
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      <footer className="relative z-10 border-t border-zinc-800/80 py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-zinc-500 text-sm">© {new Date().getFullYear()} pattrnworks. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default Careers;
